import React, { useState } from "react";
import { CreateTableNew as CreateTable } from "../../../../common/APICommon";
import SettingsBackupRestoreIcon from '@mui/icons-material/SettingsBackupRestore';
import { ConfirmationModal } from "../../../../common/Modal";
import { StatusBadge } from "../../../../common/Badge";

const LeaveRecallModule = ({
  user,
  data,
  loading,
  onRecall
}) => {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const headers = [
    "Leave Ref No.",
    "Recall Ref No.",
    "Leave Type",
    "Leave Period",
    "Total Days",
    "Recallable Date Range",
    "Recallable Days",
    "Status",
    "Actions"
  ];

  const accessors = [
    "leaveRequestReferenceNumber",
    "leaveRecallReferenceNumber",
    "leaveTypeDescription",
    "dateFrom",
    "totalDays",
    "recallableDateFrom",
    "availableRecallableDays",
    "leaveRecallStatus",
    "actions"
  ];

  const handleRecallClick = (row) => {
    setSelectedRequest(row);
    setIsConfirmOpen(true);
  };

  const handleConfirmRecall = async ({ remarks }) => {
    // MODIFIED: Pass date range details to match Option 1 payload schema
    const dates = {
      recallableDateFrom: selectedRequest?.recallableDateFrom,
      recallableDateTo: selectedRequest?.recallableDateTo
    };

    const success = await onRecall(selectedRequest.leaveRequestId, remarks, dates);
    if (success) {
      setIsConfirmOpen(false);
      setSelectedRequest(null);
    }
  };

  const actions = [
    {
      name: "Initiate Recall",
      tooltip: "Recall this leave",
      icon: <SettingsBackupRestoreIcon fontSize="small" />,
      className: "bg-orange-500 hover:bg-orange-600 flex items-center",
      function: handleRecallClick,
      visible: (row) => !row.leaveRecallStatus || row.leaveRecallStatus === "None",
    },
  ];

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "2-digit"
    });
  };

  const customRender = (row, accessor) => {
    if (accessor === "leaveRequestReferenceNumber") {
      return row[accessor] ? `LV-${row[accessor].toString().padStart(6, "0")}` : "-";
    }
    if (accessor === "leaveRecallReferenceNumber") {
      return row[accessor] ? `LRC-${row[accessor].toString().padStart(6, "0")}` : "-";
    }
    if (accessor === "dateFrom") {
      return `${formatDate(row.dateFrom)} - ${formatDate(row.dateTo)}`;
    }
    if (accessor === "recallableDateFrom") {
      const recallFrom = formatDate(row.recallableDateFrom);
      const recallTo = formatDate(row.recallableDateTo);
      // Only display range if valid non-null dates exist
      return recallFrom && recallTo ? `${recallFrom} - ${recallTo}` : "-";
    }
    if (accessor === "leaveRecallStatus") {
      return <StatusBadge status={row[accessor] || "None"} />;
    }
    return null;
  };

  return (
    <div className="p-4">
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        <CreateTable
          tableName={"Approved Leaves Eligible for Recall"}
          headers={headers}
          accessors={accessors}
          data={data || []}
          actions={actions}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          itemsPerPage={10}
          totalItems={data?.length || 0}
          customCellRenderer={customRender}
        />
      </div>

      <ConfirmationModal
        visible={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        onConfirm={handleConfirmRecall}
        title="Confirm Leave Recall"
        message={`Are you sure you want to recall the approved leave for ${selectedRequest?.leaveTypeDescription} (Ref: LV-${selectedRequest?.leaveRequestReferenceNumber?.toString().padStart(6, "0")})?`}
        noteMessage="This will submit the recall request for management and HR approval."
        confirmText="Confirm Recall"
        type="warning"
        hasRemarks={true}
        isLoading={loading}
      />
    </div>
  );
};

export default LeaveRecallModule;